import React, { useEffect, useMemo, useRef, useState } from "react";
import { resolvePath } from "@utils/resolvePath";
import { visibleClaimsSlides } from "./audience";
import {
  ClaimsRoot as Root,
  ClaimsTrack as Track,
  ClaimsCard as Card,
  Chips,
  Glow,
  Illustration,
  Dots
} from "./styles";
import type { NewCarReviewClaimsProps } from "./types";

const asset = (name: string) =>
  resolvePath(`/images/new-car-plan-positioning/${name}`);
const illustrations = {
  settlement: "claim-settlement.png",
  paid: "claims-paid.png",
  maruti: "maruti-car.png"
};

export default function NewCarReviewClaims({
  slides,
  make,
  is_visible = true
}: NewCarReviewClaimsProps) {
  const visible = useMemo(
    () => visibleClaimsSlides(slides || [], make),
    [slides, make]
  );
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    setActive(0);
    track.current?.scrollTo({ left: 0 });
  }, [visible]);
  if (!is_visible || !visible.length) return null;
  const goTo = (index: number) => {
    const node = track.current;
    const child = node?.children[index] as HTMLElement;
    if (node && child)
      node.scrollTo({
        left: child.offsetLeft - (node.children[0] as HTMLElement).offsetLeft,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth"
      });
  };
  return (
    <Root aria-label="Claims highlights">
      <Track
        ref={track}
        tabIndex={0}
        aria-label="Swipe through claims highlights"
        onKeyDown={(event) => {
          if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
            event.preventDefault();
            goTo(
              Math.max(
                0,
                Math.min(
                  visible.length - 1,
                  active + (event.key === "ArrowRight" ? 1 : -1)
                )
              )
            );
          }
        }}
        onScroll={() => {
          const node = track.current;
          if (node)
            setActive(
              Math.min(visible.length - 1, Math.round(node.scrollLeft / 276))
            );
        }}
      >
        {visible.map((slide) => (
          <Card key={slide.id} data-kind={slide.illustration}>
            <Glow
              alt=""
              data-kind={slide.illustration || "support"}
              src={asset(`${slide.illustration || "support"}-glow.svg`)}
            />
            {slide.illustration === "paid" && (
              <Glow
                alt=""
                data-kind="support"
                src={asset("paid-top-glow.svg")}
              />
            )}
            {slide.illustration && (
              <Illustration data-kind={slide.illustration}>
                <img alt="" src={asset(illustrations[slide.illustration])} />
              </Illustration>
            )}
            <h2>{slide.title}</h2>
            {slide.points ? (
              <Chips>
                {slide.points.map((point) => (
                  <span key={point}>{point}</span>
                ))}
              </Chips>
            ) : (
              <p>{slide.description}</p>
            )}
          </Card>
        ))}
      </Track>
      {visible.length > 1 && (
        <Dots>
          {visible.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`Show ${slide.title}`}
              aria-current={active === index}
              onClick={() => goTo(index)}
            />
          ))}
        </Dots>
      )}
    </Root>
  );
}
